/** Ampliar e baixar um anexo da conversa. */

const EXTENSAO_POR_MIME: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "audio/mpeg": "mp3",
  "audio/ogg": "ogg",
  "audio/webm": "webm",
  "audio/wav": "wav",
  "application/pdf": "pdf",
}

/**
 * ESQUEMAS que um anexo pode ter.
 *
 * A URL do anexo vem da MENSAGEM — do atendente, do bot, ou de quem conseguir escrever na
 * conversa. Ela ia direta para o `href` do link "Abrir" e para o `window.open` do fallback de
 * download. Com `javascript:...` ali, o clique executa script na pagina da LOJA (o widget roda
 * no site do cliente), com o que a pagina tiver de sessao.
 *
 * `blob:` entra porque e o que o proprio download cria (`URL.createObjectURL`).
 */
const ESQUEMAS_DE_ANEXO = new Set(["http:", "https:", "blob:"])

/**
 * Devolve a URL do anexo se ela for de um esquema aceitavel, senao `null`.
 *
 * Resolve contra a pagina para que caminho relativo (`/midia/x.png`) continue valendo — ele
 * herda o esquema da propria pagina, que ja passou pela mesma regra.
 */
export function urlDeAnexoSegura(url: unknown): string | null {
  const cru = typeof url === "string" ? url.trim() : ""
  if (!cru) return null

  // Base de apoio quando nao ha `window` (teste, SSR): sem ela, caminho relativo nao parseia e
  // um anexo legitimo seria recusado. Esquema absoluto na string vence a base, entao
  // `javascript:` continua caindo fora.
  const base =
    (typeof window !== "undefined" && window.location?.href) || "https://anexo.invalid/"
  try {
    const alvo = new URL(cru, base)
    return ESQUEMAS_DE_ANEXO.has(alvo.protocol) ? cru : null
  } catch {
    return null
  }
}

/**
 * O nome com que o arquivo é salvo.
 *
 * A URL do Cloudinary não termina em extensão — o `public_id` é
 * `foto_1788262389504` e o tipo vive na entrega, não no caminho. Salvar com esse
 * nome cru dá um arquivo que o sistema não sabe abrir, então a extensão vem do
 * mime quando o caminho não traz uma.
 */
export function nomeDoArquivo(url: string, mime?: string | null): string {
  let caminho = String(url || "")
  try {
    caminho = new URL(url).pathname
  } catch {
    // URL relativa ou malformada: sobra o texto cru, e o corte abaixo resolve.
  }

  const base = (caminho.split("/").pop() || "").split("?")[0]
  const limpo = decodeURIComponent(base)
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    // "nota fiscal (2)" viraria "nota_fiscal__2_": junta a corrida de traços e
    // tira os das pontas, senão o nome salvo fica com sujeira à vista.
    .replace(/_{2,}/g, "_")
    .replace(/^[_.]+|[_.]+$/g, "")
    .slice(0, 80)
  if (!limpo) return "anexo"

  const extensao = EXTENSAO_POR_MIME[String(mime || "").toLowerCase()]
  const jaTemExtensao = /\.[a-zA-Z0-9]{2,5}$/.test(limpo)
  if (jaTemExtensao || !extensao) return limpo
  return `${limpo}.${extensao}`
}

/**
 * Baixa o anexo.
 *
 * O `download` de um `<a>` é **ignorado quando o arquivo é de outro domínio** —
 * e o nosso vem do Cloudinary. Sem isto, "Baixar" apenas abria a imagem numa
 * aba. Então buscamos os bytes (o Cloudinary responde com
 * `Access-Control-Allow-Origin: *`) e salvamos a partir de um blob, que é da
 * própria página e por isso respeita o `download`.
 *
 * Se a busca falhar — rede, CORS de um dia para o outro —, abrimos numa aba
 * nova: pior que salvar, melhor que um botão que não faz nada.
 */
export async function baixarAnexo(url: string, mime?: string | null): Promise<void> {
  // Vale para os dois caminhos: o `fetch` falharia num `javascript:`, cairia no `catch` e o
  // `window.open` do fallback e que executaria o script.
  const seguro = urlDeAnexoSegura(url)
  if (!seguro) {
    console.warn("[anexo] endereco recusado (esquema nao permitido)")
    return
  }

  try {
    const resposta = await fetch(seguro, { mode: "cors", credentials: "omit" })
    if (!resposta.ok) throw new Error(String(resposta.status))
    const blob = await resposta.blob()
    // A extensão sai do que foi BAIXADO, não do que a mensagem dizia: o
    // Cloudinary entrega o original, e já vi registro com `image/png` cujos
    // bytes eram JPEG. Quem abre o arquivo depois vai pelo nome.
    const enderecoLocal = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = enderecoLocal
    link.download = nomeDoArquivo(seguro, blob.type || mime)
    document.body.appendChild(link)
    link.click()
    link.remove()
    // Revogar na hora cancela o download em alguns navegadores.
    setTimeout(() => URL.revokeObjectURL(enderecoLocal), 60_000)
  } catch {
    window.open(seguro, "_blank", "noopener,noreferrer")
  }
}
